import React, { useCallback, useEffect, useMemo, useState } from 'react';

// material-ui
import {
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Dialog,
    Divider,
    Grid,
    Tooltip,
    Typography,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import stcho from '../../../assets/stc_ho.png';
import { TCLabel, TCTitleLabel } from 'components/TopCourtComponents/Label/TopCourtLabel';
import TableComponent from 'components/TopCourtComponents/Table/TableComponent';
import PaymentFilter from './paymentFilter';
import PaymentSummmary from './paymentSummary';
import MerchantDeposit from '../merchant/component/merchantDeposit';

import { useDebounce } from 'use-debounce';
import moment from 'moment';
import { toast } from 'react-toastify';

import { currencyFormat } from 'helper/NumberFormat';
import { getUrlCourtCode } from 'helper/Court';
import { checkIsSAdmin, isMerchant } from 'helper/Admin';

import { TypeSelectOption } from '../players/playerFilter';
import { getPaymentSummary, getPayments } from 'services/paymentService';
import { createMerchantDeposit, getMerchantDepositList } from 'services/merchantServices';

import { orderType, providerType, color } from 'constant/constant';

import raining from '../../../assets/images/raining.png';

const TOPUP_OPTIONS = [
    { value: 2000000, label: 'IDR 2,000,000' },
    { value: 5000000, label: 'IDR 5,000,000' },
    { value: 10000000, label: 'IDR 10,000,000' },
];

const initFilter = {
    load: true,
    paymentDateStart: moment().startOf('month').format('YYYY-MM-DDT17:00:00.000'),
    paymentDateEnd: moment().set({ hour: 23, minute: 59, second: 59, millisecond: 59 }).toISOString(),
    paymentDateType: TypeSelectOption[0],
    provider: null,
    type: null,
    amountMin: null,
    amountMax: null,
    amountType: TypeSelectOption[0],
    imeiRepeated: '',
    referenceId: '',
    rainRefund: null,
    page: 0,
    limit: 20,
};

export default function Payments() {
    const merchantId = isMerchant() || '';
    const courtPlace = localStorage.getItem('courtPlace') ? JSON.parse(localStorage.getItem('courtPlace'))?.id == '69b3a2e8c994e1353f8b2074' : null || null

    const place = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('court_place') || '[]');
        } catch {
            return [];
        }
    }, []);

    const [filter, setFilter] = useState(initFilter);
    const [debouncedFilter] = useDebounce(filter, 500);

    const [openTopUp, setOpenTopUp] = useState(false);
    const [topUpAmount, setTopUpAmount] = useState('0');

    const [merchantBalance, setMerchantBalance] = useState(0);
    const [merchantDepositList, setMerchantDepositList] = useState([]);
    const [totalDataDepositList, setTotalDataDepositList] = useState(0)
    const [depositFilter, setDepositFilter] = useState({
        page: initFilter.page,
        limit: initFilter.limit
    })

    const [totalData, setTotalData] = useState(0);
    const [rows, setRows] = useState([]);

    const [summary, setSummary] = useState({
        grandTotal: 0,
        totalVA: 0,
        totalSTCHO: {
            totalOrder: 0,
            valuePack: 0,
            booking: 0,
            class: 0,
            service: 0,
            rent: 0,
            buy: 0,
        },
        totalQRIS: 0,
        totalTXNFee: 0,
        merchantBalance: 0,
        totalVPAmount: 0,
    });

    const isMerchantAdmin = Boolean(isMerchant() && checkIsSAdmin());
    const canTopUp = topUpAmount && topUpAmount !== '0';

    const buildPaymentPayload = useCallback((f) => {
        return {
            page: f.page,
            paymentDateStart: f.paymentDateStart || '',
            paymentDateEnd: f.paymentDateEnd || '',
            paymentDateType: (f.paymentDateType && f.paymentDateType.value) || '',
            provider: (f.provider && f.provider.value) || '',
            type: (f.type && f.type.value) || '',
            amountMin: f.amountMin || '',
            amountMax: f.amountMax || '',
            amountType: (f.amountType && f.amountType.value) || '',
            imeiRepeated: f.imeiRepeated || '',
            referenceId: f.referenceId || '',
            rainRefund: (f.rainRefund && f.rainRefund.value) || '',
        };
    }, []);

    const loadPayment = useCallback(async () => {
        try {
            const { data, error } = await getPayments(buildPaymentPayload(filter));
            if (error) throw error;
            if (data) {
                setRows(data.order || []);
                setTotalData(data.totalData || 0);
            }
        } catch (e) {
            console.error(e);
        }
    }, [buildPaymentPayload, filter]);

    const loadSummary = useCallback(async () => {
        try {
            const payload = buildPaymentPayload(filter);
            const { data, error } = await getPaymentSummary(payload);
            if (error) throw error;

            if (data) {
                setSummary({
                    grandTotal: data.grandTotal,
                    totalSTCHO: data.totalSTCHO,
                    totalQRIS: data.totalQRIS,
                    merchantBalance: data.merchantBalance,
                    totalVPAmount: data.totalVPAmount,
                    totalTXNFee: data.totalTXNFee
                });
                setMerchantBalance(Number(data.merchantBalance || 0));
            }
        } catch (e) {
            console.error(e);
        }
    }, [buildPaymentPayload, filter]);

    const loadMerchantDepositRequest = useCallback(async () => {
        if (!merchantId) return;
        try {
            const { data, error } = await getMerchantDepositList({
                merchantId: merchantId,
                isClosed: '',
                page: depositFilter.page,
                limit: depositFilter.limit
            });
            if (error) throw error;
            if (data) {
                setMerchantDepositList(data.merchantDeposit);
                setTotalDataDepositList(data.totalData)
            }
        } catch (e) {
            console.error(e);
        }
    }, [merchantId, depositFilter]);

    useEffect(() => {
        loadPayment();
        loadSummary();
        if (merchantId) loadMerchantDepositRequest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (debouncedFilter.load === true) {
            loadPayment();
            loadSummary();
        }
    }, [debouncedFilter, loadPayment, loadSummary]);

    const onChangeFilter = (patch) => {
        setFilter((prev) => ({
            ...prev,
            ...patch,
            page: 0,
        }));
    };

    const onChangePage = (patch) => {
        setFilter((prev) => ({
            ...prev,
            ...patch,
        }));
    };

    const clearFilter = () => setFilter(initFilter);

    const handleOpenTopUp = () => {
        setTopUpAmount('0');
        setOpenTopUp(true);
    };

    const handleCloseTopUp = () => setOpenTopUp(false);

    const handleSelectTopUp = (amount) => {
        setTopUpAmount(String(amount));
    };

    const topUp = async (amountStr) => {
        try {
            const n = Number(amountStr);
            if (!Number.isFinite(n) || n <= 0) {
                toast.error('Invalid amount');
                return;
            }

            const { data, error } = await createMerchantDeposit({ deposit: n });
            if (error) throw error;

            if (data) {
                toast.success('Top up requested. Pending approval');
                setDepositFilter({ page: 0, limit: 20 })
                loadMerchantDepositRequest();
            }
        } catch (e) {
            console.error(e);
            toast.error('Request failed. Please try again later');
        }
    };

    const valueCustomator = (row) => {
        const placeCode = (place.find((court) => court.id === row.placeId)?.code || '').toUpperCase();

        const userLabel = merchantId
            ? row.user?.name || (row.createdBy ? `${row.createdBy.name} ${placeCode}` : '-')
            : row.user?._id && row.user?.name
                ? (
                    <span
                        className="span-link"
                        onClick={() => {
                            window.location.href = `/${getUrlCourtCode()}/users/detail/${row.user._id}`;
                        }}
                    >
                        {row.user.name}
                    </span>
                )
                : row.createdBy
                    ? `${row.createdBy.name} ${placeCode}`
                    : '-';

        return {
            paymentDate: <TCLabel>{moment(row?.orderStatus?.[1]?.date).format('YYYY/MM/DD HH:mm:ss')}</TCLabel>,

            orderId: (
                <Grid>
                    {!merchantId ? (
                        <a href={`/${getUrlCourtCode()}/orders/${row._id}`} style={{ color: 'black' }}>
                            {row.bookingId}
                        </a>
                    ) : (
                        row.bookingId
                    )}
                </Grid>
            ),

            user: userLabel,

            provider: <TCLabel>{providerType(row.paymentType)}</TCLabel>,
            type: <TCLabel>{orderType(row.orderType)}</TCLabel>,
            amount: <TCLabel>{currencyFormat(row.total)}</TCLabel>,

            ...(merchantId && {
                valuePackAmount: <TCLabel>{currencyFormat(row.totalVP || 0)}</TCLabel>,
                txnFee: (
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <TCLabel>{currencyFormat(row.txnFee || 0)}</TCLabel>

                        {row.parentOrder ? (
                            <Tooltip
                                title={
                                    <div>
                                        {(row.childrenOrders || []).map((v) => (
                                            <div key={v?._id || v?.bookingId}>Include: {v.bookingId}</div>
                                        ))}
                                    </div>
                                }
                            >
                                <InfoOutlined sx={{ width: 16, height: 16, color: color.blueIndoor_2 }} />
                            </Tooltip>
                        ) : null}
                    </Box>
                ),
            }),

            ...(courtPlace && {
                valuePackAmount: <TCLabel>{currencyFormat(row.totalVP || 0)}</TCLabel>,
            }),

            ...(!merchantId && !courtPlace
                ? {
                    rainRefund: (
                        <div style={{ height: 20 }}>
                            {row.isRefund ? <img src={raining} width={25} height={25} alt="" /> : '-'}
                        </div>
                    ),
                }
                : {}),
        };
    };

    return (
        <Grid>
            {isMerchantAdmin && (
                <Grid
                    item
                    xs={12}
                    md={3}
                    mb={2}
                    mt={-6}
                    sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}
                >
                    <Grid>
                        <TCLabel subtitle>My Balance</TCLabel>
                        <TCLabel bold style={{ color: (summary?.merchantBalance || 0) < 0 ? "red" : 'black' }}>{currencyFormat(summary?.merchantBalance || 0)}</TCLabel>
                    </Grid>

                    <Button variant="contained" size="small" sx={{ height: '40px', px: 3 }} onClick={handleOpenTopUp}>
                        Topup
                    </Button>
                </Grid>
            )}

            <PaymentFilter filter={filter} onChangeFilter={onChangeFilter} clearFilter={clearFilter} merchantId={merchantId} courtPlace={courtPlace} />
            <PaymentSummmary summary={summary} />

            <TableComponent
                columns={[
                    { label: 'Payment Date' },
                    { label: 'Booking Id' },
                    { label: 'User' },
                    { label: 'Provider' },
                    { label: 'Type' },
                    { label: 'Amount' },
                    ...(merchantId ? [{ label: 'VP Amount' }, { label: 'Txn Fee' }] : []),
                    ...(courtPlace ? [{ label: 'VP Amount' }] : []),
                    ...(!merchantId && !courtPlace ? [{ label: 'Rain Refund' }] : []),
                ]}
                totalData={totalData}
                rows={rows}
                valueCustomator={valueCustomator}
                page={filter.page}
                pageChange={(page) => onChangePage({ page })}
                rowWidth={{
                    paymentDate: '180px',
                    orderId: '150px',
                    user: '150px',
                    provider: '100px',
                    type: '100px',
                    amount: '150px',
                    ...(merchantId && { valuePackAmount: '150px', txnFee: '150px' }),
                    rainRefund: '120px',
                }}
            />

            {/* Top Up Dialog */}
            <Dialog
                open={openTopUp}
                onClose={handleCloseTopUp}
                fullWidth
                maxWidth="md"
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <Card sx={{ overflowY: 'scroll' }}>
                    <Grid sx={{ p: 3 }}>
                        <TCLabel bold style={{ fontSize: 16 }}>
                            My Balance
                        </TCLabel>

                        <Box
                            sx={{
                                backgroundColor: '#f0f7ff',
                                p: 2,
                                borderRadius: 1,
                                mb: 2,
                                borderLeft: '5px solid #2e7d32',
                            }}
                        >
                            <TCLabel bold style={{ fontSize: 16, color: merchantBalance < 0 ? 'red' : '#2e7d32' }}>
                                {currencyFormat(merchantBalance || 0)}
                            </TCLabel>
                        </Box>

                        <TCLabel>Top Up Amount</TCLabel>
                        <TCLabel subtitle>Scan QR to make payment and click confirm payment after</TCLabel>
                        <Grid container>
                            <Grid container xs={12} md={12} item spacing={1} sx={{ mt: 0.5 }}>
                                {TOPUP_OPTIONS.map((opt) => {
                                    const selected = topUpAmount === String(opt.value);

                                    return (
                                        <Grid key={opt.value} item xs={6} sm={4}>
                                            <Card
                                                variant="outlined"
                                                sx={{
                                                    borderRadius: 2,
                                                    borderColor: selected ? '#0198E1' : 'rgba(0,0,0,0.12)',
                                                    bgcolor: selected ? 'rgba(1,152,225,0.08)' : 'transparent',
                                                    transition: 'all .15s ease',
                                                    '&:hover': {
                                                        borderColor: '#0198E1',
                                                        boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                                                    },
                                                }}
                                            >
                                                <CardActionArea onClick={() => handleSelectTopUp(opt.value)}>
                                                    <CardContent sx={{ py: 1.5 }}>
                                                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                                            <TCLabel>{opt.label}</TCLabel>
                                                            <Box
                                                                sx={{
                                                                    width: 10,
                                                                    height: 10,
                                                                    borderRadius: '50%',
                                                                    bgcolor: selected ? '#0198E1' : 'rgba(0,0,0,0.15)',
                                                                }}
                                                            />
                                                        </Box>
                                                    </CardContent>
                                                </CardActionArea>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Grid>

                        <Grid container item xs={12} md={12} mt={2} justifyContent="center">
                            {
                                canTopUp &&
                                <img src={stcho} width={180} height={200} alt=''/>
                            }
                        </Grid>

                        <Grid container xs={12} spacing={1} pt={2} justifyContent="right">
                            <Grid item>
                                <Button onClick={handleCloseTopUp}>Cancel</Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    style={{ backgroundColor: !canTopUp ? '#c8ced3' : '#0198E1' }}
                                    disabled={!canTopUp}
                                    onClick={() => {
                                        topUp(topUpAmount);
                                        handleCloseTopUp();
                                    }}
                                >
                                    Confirm Payment
                                </Button>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />

                        <Box>
                            <TCTitleLabel>Deposit History</TCTitleLabel>
                            <MerchantDeposit
                                rows={merchantDepositList}
                                totalData={totalDataDepositList}
                                loading={false}
                                filter={depositFilter}
                                onChangePage={(e) => {
                                    setDepositFilter({
                                        page: e.page
                                    })
                                }}
                                merchantId={merchantId}
                                refetch={loadMerchantDepositRequest}
                                fromBalance
                            />
                        </Box>
                    </Grid>
                </Card>
            </Dialog>
        </Grid>
    );
}